import api from "./api";
import { withRetry, logError, getErrorMessage, toastError } from "../utils/errorHandler";

export const authService = {
  // Login user
  async login(email, password) {
    try {
      const response = await withRetry(
        () => api.post("/auth/login", { email, password }),
        2, // Only retry once for login
        "login"
      );

      // Store token and user data
      if (response.data.data.accessToken) {
        localStorage.setItem("token", response.data.data.accessToken);
      }
      if (response.data.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
      }

      return response.data.data;
    } catch (error) {
      logError(error, "login", { email });
      toastError(error, "login");
      const message = getErrorMessage(error, "login");
      throw new Error(message);
    }
  },

  // Register user
  async register(userData) {
    try {
      const response = await withRetry(
        () => api.post("/auth/register", userData),
        2,
        "register"
      );

      // Store token and user data if provided
      if (response.data.data.accessToken) {
        localStorage.setItem("token", response.data.data.accessToken);
      }
      if (response.data.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
      }

      return response.data;
    } catch (error) {
      logError(error, "register", { email: userData.email });
      toastError(error, "register");
      const message = getErrorMessage(error, "register");
      throw new Error(message);
    }
  },

  // Get current user
  async getCurrentUser() {
    try {
      const response = await withRetry(
        () => api.get("/auth/me"),
        3,
        "getCurrentUser"
      );

      // Update stored user data
      if (response.data.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
      }

      return response.data.data.user;
    } catch (error) {
      console.error("getCurrentUser error:", error);
      logError(error, "getCurrentUser");
      toastError(error, "profile");
      const message = getErrorMessage(error, "profile");
      throw new Error(message);
    }
  },

  // Logout user
  async logout() {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Even if logout fails on server, we still remove local data
      logError(error, "logout");
    } finally {
      // Always clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
  },

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await withRetry(
        () => api.put("/auth/profile", userData),
        3,
        "updateProfile"
      );

      // Update stored user data
      if (response.data.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
      }

      return response.data.data;
    } catch (error) {
      logError(error, "updateProfile", { userId: userData.id });
      toastError(error, "profile");
      const message = getErrorMessage(error, "profile");
      throw new Error(message);
    }
  },

  // Get user orders
  async getUserOrders() {
    try {
      const response = await withRetry(
        () => api.get("/users/orders"),
        3,
        "getUserOrders"
      );
      return response.data;
    } catch (error) {
      logError(error, "getUserOrders");
      toastError(error, "orders");
      const message = getErrorMessage(error, "orders");
      throw new Error(message);
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem("token");
  },

  // Get stored user data
  getStoredUser() {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      logError(error, "getStoredUser");
      return null;
    }
  },

  // Get stored token
  getToken() {
    return localStorage.getItem("token");
  },
};

export default authService;
